import React, { useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

const useStyles = createUseStyles({
  container: {
    width: 120,
    height: 120,
    margin: '1rem',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 4,
    display: 'block',
    backgroundColor: 'transparent'
  },
  img: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    userSelect: 'none',
    transitionProperty: 'opacity, transform',
    transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)'
  },
  imgHidden: {
    opacity: 0,
    transform: 'scale(1.02)'
  },
  imgVisible: {
    opacity: 1,
    transform: 'scale(1)'
  },
  deleteBtn: {
    position: 'absolute !important', // !important pour surcharger les styles MUI par défaut si besoin
    top: 4,
    right: 4,
    zIndex: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.4) !important',
    color: 'white !important',
    padding: '2px !important',
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.9) !important' // Couleur "error" de MUI au survol
    }
  }
})

const resolvedSrcCache = {}

const bufferToBase64 = (buffer) =>
  btoa(new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''))

const Image = ({ img, mode = 'view', onDelete }) => {
  const classes = useStyles()
  const [displayedSrc, setDisplayedSrc] = useState(null)
  const [pendingSrc, setPendingSrc] = useState(null)
  const [failed, setFailed] = useState(false)

  const inFlightRef = useRef(null)
  const createdBlobUrlsRef = useRef([])

  const TRANSITION_MS = 300
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  const effectiveTransition = reduceMotion ? 0 : TRANSITION_MS

  useEffect(() => {
    setFailed(false)

    if (!img) return

    let currentUrl = null

    // Résolution de la source de l'image
    if (img.url) currentUrl = img.url
    else if (typeof img === 'string') currentUrl = img
    else if (img.file instanceof File) {
      const obj = URL.createObjectURL(img.file)
      createdBlobUrlsRef.current.push(obj)
      setDisplayedSrc(obj)
      return
    } else if (img.file?.url) currentUrl = img.file.url
    else if (img.file?.type === 'Buffer' && Array.isArray(img.file.data)) {
      const dataUrl = `data:image/png;base64,${bufferToBase64(img.file.data)}`
      setDisplayedSrc(dataUrl)
      return
    }

    if (!currentUrl) return

    // Vérification du cache
    if (resolvedSrcCache[currentUrl]) {
      const resolved = resolvedSrcCache[currentUrl]
      if (!displayedSrc) setDisplayedSrc(resolved)
      else if (displayedSrc !== resolved) setPendingSrc(resolved)
      return
    }

    const token = Symbol('imgToken')
    inFlightRef.current = token

    const tester = new window.Image()

    tester.onload = () => {
      if (inFlightRef.current !== token) return
      resolvedSrcCache[currentUrl] = currentUrl
      if (!displayedSrc) setDisplayedSrc(currentUrl)
      else if (displayedSrc !== currentUrl) setPendingSrc(currentUrl)
    }

    tester.onerror = async () => {
      if (inFlightRef.current !== token) return
      try {
        const resp = await fetch(currentUrl, { credentials: 'include' })
        if (!resp.ok) throw new Error('fetch failed')
        const blob = await resp.blob()
        const blobUrl = URL.createObjectURL(blob)
        createdBlobUrlsRef.current.push(blobUrl)
        resolvedSrcCache[currentUrl] = blobUrl

        if (inFlightRef.current !== token) {
          URL.revokeObjectURL(blobUrl)
          return
        }
        if (!displayedSrc) setDisplayedSrc(blobUrl)
        else if (displayedSrc !== blobUrl) setPendingSrc(blobUrl)
      } catch (e) {
        console.error('[Image] Fallback failed for', currentUrl, e)
        if (inFlightRef.current !== token) return
        if (!displayedSrc) setFailed(true)
      }
    }

    tester.src = currentUrl

    return () => {
      if (inFlightRef.current === token) inFlightRef.current = null
      tester.onload = null
      tester.onerror = null
    }
  }, [img]) // On relance si l'objet image change

  // Finalisation du crossfade
  useEffect(() => {
    if (!pendingSrc) return
    const t = setTimeout(() => {
      setDisplayedSrc(pendingSrc)
      setPendingSrc(null)
    }, effectiveTransition + 50) // Marge de sécurité pour la transition CSS
    return () => clearTimeout(t)
  }, [pendingSrc, effectiveTransition])

  // Nettoyage des Blob URLs à l'unmount
  useEffect(() => {
    return () => {
      createdBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u))
      createdBlobUrlsRef.current = []
    }
  }, [])

  if ((!displayedSrc && !pendingSrc && !failed) || (failed && !displayedSrc)) {
    return null
  }

  const handleDelete = (e) => {
    e.stopPropagation()
    if (onDelete) onDelete()
  }

  return (
    <Box className={classes.container} component='div' role='img' aria-label='Aperçu image'>
      {mode === 'edit' && (
        <IconButton className={classes.deleteBtn} onClick={handleDelete} size='small'>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}

      {displayedSrc && (
        <img
          src={displayedSrc}
          alt=''
          className={`${classes.img} ${pendingSrc ? classes.imgHidden : classes.imgVisible}`}
          style={{ transitionDuration: `${effectiveTransition}ms` }}
          draggable={false}
          onError={() => !pendingSrc && setFailed(true)}
        />
      )}

      {pendingSrc && (
        <img
          src={pendingSrc}
          alt=''
          className={`${classes.img} ${classes.imgVisible}`}
          style={{
            opacity: 0,
            transitionDuration: `${effectiveTransition}ms`
          }}
          onLoad={(e) => (e.currentTarget.style.opacity = '1')}
          onError={() => setPendingSrc(null)}
          draggable={false}
        />
      )}
    </Box>
  )
}

export default Image
